import { Badge, BadgeVariant, Box, Button, useOpenClose } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { TrancheStatus } from 'types';

import { useGetFundQuery } from '../../services/funds';
import { PropertyValue } from '../PropertyListBox/PropertyValue';
import { SkeletonBox } from '../SkeletonBox';

import { EditTrancheModal } from './EditTrancheModal';
import { FundAccountsSummary } from './FundAccountsSummary';

interface TrancheExportButtonProps {
  trancheName: string;
  onExport: (trancheName: string) => void;
}

function TrancheExportButton({ trancheName, onExport }: TrancheExportButtonProps) {
  const handlePress = useCallback(() => onExport(trancheName), [trancheName, onExport]);
  return (
    <Button
      iconPrefix="download"
      variant="tertiary"
      tone="neutral"
      size="xs"
      aria-label="Export Accounts"
      title="Export Accounts"
      onClick={handlePress}
    />
  );
}

const convertIsoToDateMed = (isoDate: string, opts: { zone?: string } = {}) =>
  isoDate ? DateTime.fromISO(isoDate, opts).toLocaleString(DateTime.DATE_MED) : null;

const getTrancheFilter = (trancheName: string) =>
  `[{"id": "tranche", "operation": "Equal", "selectedValues": ["${trancheName}"]}]`;

const trancheStatus: {
  [key in TrancheStatus]: {
    label: string;
    variant: BadgeVariant;
  };
} = {
  [TrancheStatus.open]: {
    label: 'Open',
    variant: 'info',
  },
  [TrancheStatus.closed]: {
    label: 'Closed',
    variant: 'default',
  },
};

export function FundOverview() {
  const navigate = useNavigate();
  const { key } = useParams<{ key: any }>();
  const { data: fund = {}, isFetching } = useGetFundQuery(key);
  const { ptoGreenlight = false } = useFlags();
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const [selectedTranche, setSelectedTranche] = useState<any>(null);

  const handleEdit = useCallback(
    (tranche: any) => {
      setSelectedTranche(tranche);
      openModal();
    },
    [openModal],
  );

  const handleExportAccounts = useCallback(
    (trancheName: string) => {
      const params = new URLSearchParams({ trancheName });
      navigate(`/admin/document-pull?${params.toString()}`);
    },
    [navigate],
  );

  return (
    <Box direction="row" childGap="3xl">
      <Box style={{ minWidth: '320px' }}>
        <Box margin="0 0 lg 0" childGap={'xs'}>
          <Button size="sm" navigate={() => navigate(`/admin/funds/${key}/assign`)} isLoading={isFetching}>
            Tranche Accounts
          </Button>
          {ptoGreenlight && (
            <Button
              size="sm"
              variant="secondary"
              navigate={() => navigate(`/admin/funds/${key}/unassign`)}
              isLoading={isFetching}
            >
              Un-tranche Accounts
            </Button>
          )}
        </Box>
        <Box childGap="xs" fontSize="xs" margin="0 0 lg 0">
          <PropertyValue
            label="NetSuite Subsidiary Id"
            value={isFetching ? '-' : fund?.netSuiteSubsidiaryId || '-'}
          />
          <PropertyValue label="Concord Lender Code" value={isFetching ? '-' : fund?.concordLenderCode || 'N/A'} />
          <PropertyValue
            label="Created"
            value={isFetching ? '-' : (convertIsoToDateMed(fund?.createdDate) as string)}
          />
          <PropertyValue
            label="Commitment Expiration Date"
            value={
              isFetching
                ? '-'
                : ((fund?.commitmentExpirationDate
                    ? convertIsoToDateMed(fund.commitmentExpirationDate, { zone: 'utc' })
                    : 'N/A') as string)
            }
          />
          <PropertyValue
            label="Fund Completion Date"
            value={
              isFetching
                ? '-'
                : ((fund?.fundCompletionDate
                    ? convertIsoToDateMed(fund.fundCompletionDate, { zone: 'utc' })
                    : 'N/A') as string)
            }
          />
        </Box>
        <Box childGap="lg" radius="md" borderWidth="xs" borderColor="separator" padding="lg" margin="0 0 md 0">
          <Box as="h3" fontWeight="medium" fontSize="md">
            Tranches
          </Box>
          <Box>
            {isFetching ? (
              <Box padding="0 0 sm 0">
                <Box>
                  <Box direction="row" alignItems="baseline" justifyContent="space-between" padding="0 0 xs 0">
                    <Box as="h3" fontWeight="medium" fontSize="sm">
                      <SkeletonBox height="20px" width="250px" />
                    </Box>
                  </Box>
                  <Box direction="row" gap="2xs" justifyContent="space-between">
                    <Box color="body-secondary" fontWeight="medium">
                      <SkeletonBox height="14px" width="90px" />
                    </Box>
                  </Box>
                  <Box direction="row" alignItems="baseline" padding="0 0 xs 0" childGap="lg">
                    <Box childGap="xs" fontSize="xs" flex="auto" padding="xs 0 0 0">
                      <Box direction="row" gap="2xs" justifyContent="space-between">
                        <Box color="body-secondary" fontWeight="medium">
                          Submitted
                        </Box>
                        <Box
                          direction="row"
                          childGap="xs"
                          alignItems="center"
                          textAlign="right"
                          style={{ wordBreak: 'break-word' }}
                        >
                          <SkeletonBox height="14px" width="90px" />
                        </Box>
                      </Box>
                      <Box direction="row" gap="2xs" justifyContent="space-between">
                        <Box color="body-secondary" fontWeight="medium">
                          Accepted (aka Tranche Date)
                        </Box>
                        <Box
                          direction="row"
                          childGap="xs"
                          alignItems="center"
                          textAlign="right"
                          style={{ wordBreak: 'break-word' }}
                        >
                          <SkeletonBox height="14px" width="90px" />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ) : (
              fund?.tranches?.map((tranche: any) => (
                <Box padding="0 0 sm 0" key={tranche.name}>
                  <Box>
                    <Box padding="0 0 xs 0">
                      <Box as="h3" fontWeight="medium" fontSize="sm">
                        <Link
                          to={`/admin/funds/${fund.fundKey}?advancedFilters=${encodeURIComponent(getTrancheFilter(tranche.name))}`}
                        >
                          {tranche.name}
                        </Link>
                      </Box>
                    </Box>
                    <Box direction="row" alignItems="center" justifyContent="space-between" padding="xs 0">
                      <Box>
                        {ptoGreenlight && tranche.trancheStatus && (
                          <Badge
                            variant={trancheStatus[tranche.trancheStatus as TrancheStatus]?.variant}
                            message={trancheStatus[tranche.trancheStatus as TrancheStatus]?.label}
                            size={{ base: 'md', desktop: 'lg' }}
                          />
                        )}
                      </Box>
                      <Box direction="row" alignItems="center">
                        <Button
                          onClick={() => handleEdit(tranche)}
                          variant="primary"
                          tone="neutral"
                          size="xs"
                          aria-label="Edit Tranche Account"
                        >
                          Edit
                        </Button>
                        <Box margin="0 0 0 sm">
                          <TrancheExportButton trancheName={tranche.name} onExport={handleExportAccounts} />
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      direction="row"
                      alignItems="baseline"
                      // justifyContent="space-between"
                      padding="0 0 xs 0"
                      childGap="lg"
                    >
                      <Box childGap="xs" fontSize="xs" flex="auto" padding="xs 0 0 0">
                        <Box direction="row" gap="2xs" justifyContent="space-between">
                          <Box color="body-secondary" fontWeight="medium">
                            Submitted
                          </Box>
                          <Box
                            direction="row"
                            childGap="xs"
                            alignItems="center"
                            textAlign="right"
                            style={{ wordBreak: 'break-word' }}
                          >
                            {convertIsoToDateMed(tranche.submittedDate)}
                          </Box>
                        </Box>
                        <Box direction="row" gap="2xs" justifyContent="space-between">
                          <Box color="body-secondary" fontWeight="medium">
                            Accepted (aka Tranche Date)
                          </Box>
                          <Box
                            direction="row"
                            childGap="xs"
                            alignItems="center"
                            textAlign="right"
                            style={{ wordBreak: 'break-word' }}
                          >
                            {tranche.trancheDate ? convertIsoToDateMed(tranche.trancheDate) : '-'}
                          </Box>
                        </Box>
                        <Box
                          direction="row"
                          alignItems="baseline"
                          justifyContent="space-between"
                          borderWidth="0 0 xs 0"
                          borderColor="separator"
                          padding="0 0 xs 0"
                          childGap="lg"
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Box>
      <Box flex="auto">
        <FundAccountsSummary fundKey={key} />
      </Box>
      <EditTrancheModal
        fundKey={key}
        selectedTranche={selectedTranche}
        isModalOpen={isModalOpen}
        closeModal={closeModal}
      />
    </Box>
  );
}
